import React, { useEffect, useState } from "react";
import SummaryApi from "../common/index"; // adjust path as needed

const TeamLeadTaskReport = () => {
  const [filterRange, setFilterRange] = useState("today"); // default as Today
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  const fetchTeamLeadReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.getTLTaskCount.url, {
        method: "GET",
        headers: headers,
      });
      const json = await response.json();

      if (json.status && Array.isArray(json.data)) {
        setReportData(json.data);
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching TL report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to filter data based on the selected range
  const getFilteredData = (dataToFilter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of day

    return dataToFilter.filter((item) => {
      if (!item.created_date) return false;

      const itemDate = new Date(item.created_date.split(" ")[0]); // Get date part only
      itemDate.setHours(0, 0, 0, 0); // Normalize itemDate to start of day

      switch (filterRange) {
        case "today":
          return itemDate.getTime() === today.getTime();
        case "yesterday":
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        case "last7":
          const last7Start = new Date(today);
          last7Start.setDate(today.getDate() - 6); // 7 days including today
          return itemDate >= last7Start && itemDate <= today;
        case "last30":
          const last30Start = new Date(today);
          last30Start.setDate(today.getDate() - 29); // 30 days including today
          return itemDate >= last30Start && itemDate <= today;
        case "thisMonth":
          const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDayThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return itemDate >= firstDayThisMonth && itemDate <= lastDayThisMonth;
        case "lastMonth":
          const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          return itemDate >= firstDayLastMonth && itemDate <= lastDayLastMonth;
        case "all":
        default:
          return true; // No date filtering
      }
    });
  };

  useEffect(() => {
    fetchTeamLeadReport();
  }, []);

  // Function to get unique team leads and calculate their counts based on filtered students
  const getAggregatedData = (data) => {
    const aggregatedMap = new Map();

    // First, group all students by employee_id
    const studentsByEmployee = new Map();
    data.forEach(item => {
        if (!studentsByEmployee.has(item.employee_id)) {
            studentsByEmployee.set(item.employee_id, {
                employee_id: item.employee_id,
                full_name: item.full_name,
                mobile_number: item.mobile_number,
                students: [] // All raw student records for this employee
            });
        }
        studentsByEmployee.get(item.employee_id).students.push(item);
    });

    // Now, for each employee, calculate the counts from their students list
    studentsByEmployee.forEach(employee => {
        const totalCount = employee.students.length;
        const interestedCount = employee.students.filter(s => s.is_interested === "Interested").length;
        const mostInterestedCount = employee.students.filter(s => s.is_interested === "Most Interested").length;

        aggregatedMap.set(employee.employee_id, {
            employee_id: employee.employee_id,
            full_name: employee.full_name,
            mobile_number: employee.mobile_number,
            total_count: totalCount,
            interested_count: interestedCount,
            most_interested_count: mostInterestedCount,
            // Store all students for the pop-up details
            all_students: employee.students,
        });
    });

    return Array.from(aggregatedMap.values());
  };


  const getStudentsForCategory = (employeeId, category) => {
    // We get the original (unfiltered by date range yet) list of students for the employee
    // from the raw reportData, then filter it by category and then by date.
    const employeeRawData = reportData.filter(item => item.employee_id === employeeId);
    let studentsFilteredByCategory = [];

    switch (category) {
      case "total":
        studentsFilteredByCategory = employeeRawData;
        break;
      case "interested":
        studentsFilteredByCategory = employeeRawData.filter(item => item.is_interested === "Interested");
        break;
      case "most_interested":
        studentsFilteredByCategory = employeeRawData.filter(item => item.is_interested === "Most Interested");
        break;
      default:
        break;
    }
    // Now, apply the date range filter to the category-filtered students
    return getFilteredData(studentsFilteredByCategory);
  };


  const getDateLabel = () => {
    const today = new Date();

    const formatDate = (date) => {
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
    };

    switch (filterRange) {
      case "today":
        return `Today: ${formatDate(today)}`;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return `Yesterday: ${formatDate(yesterday)}`;
      }
      case "last7": {
        const last7Start = new Date(today);
        last7Start.setDate(last7Start.getDate() - 6);
        return `Last 7 Days: ${formatDate(last7Start)} - ${formatDate(today)}`;
      }
      case "last30": {
        const last30Start = new Date(today);
        last30Start.setDate(last30Start.getDate() - 29);
        return `Last 30 Days: ${formatDate(last30Start)} - ${formatDate(today)}`;
      }
      case "thisMonth": {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return `This Month: ${formatDate(firstDay)} - ${formatDate(lastDay)}`;
      }
      case "lastMonth": {
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return `Last Month: ${formatDate(firstDayLastMonth)} - ${formatDate(lastDayLastMonth)}`;
      }
      case "customRange":
        return "Custom Range";
      case "all":
      default:
        return "All Dates";
    }
  };


  // New `openInNewTab` function for TeamLeadTaskReport
  const openInNewTab = (
    dataArray,
    columnTitle,
    employeeName = "",
    showEmployeeName = false
  ) => {
    const newTab = window.open();

    let recordsPerPage = 10;
    let currentPage = 1;
    const totalRecords = dataArray.length;

    // Helper function to format date/time
    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      const options = {
        year: 'numeric',
        month: 'long', // Full month name (e.g., January)
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true, // Use 12-hour format with AM/PM
      };
      return date.toLocaleDateString('en-US', options);
    };

    const renderTable = (page, recordsPerPage) => {
      const startIndex = (page - 1) * recordsPerPage;
      const endIndex = Math.min(page * recordsPerPage, totalRecords);
      const currentRecords = dataArray.slice(startIndex, endIndex);

      return currentRecords
        .map(
          (student) => `
            <tr style="border-bottom: 1px solid #e2e8f0; text-align: center;">
              ${showEmployeeName ? `<td style="padding: 8px 16px;">${student.full_name || 'N/A'}</td>` : ""}
              <td style="padding: 8px 16px;">${student.enrollment_code || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.student_name || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.mobile_number1 || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.district || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.city || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.clg_name || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.is_interested || 'N/A'}</td>
              <td style="padding: 8px 16px;">${formatDateTime(student.next_followup_date_time)}</td>
              <td style="padding: 8px 16px;">${student.lead_description || 'N/A'}</td>
              <td style="padding: 8px 16px;">${student.is_active === '1' ? 'Yes' : 'No'}</td>
              <td style="padding: 8px 16px;">${formatDateTime(student.created_date)}</td>
            </tr>
          `
        )
        .join("");
    };

    const tableHTML = `
      <html>
        <head>
          <title>Students Data - ${columnTitle}</title>
          <style>
            body { font-family: sans-serif; padding: 24px; background-color: #f7fafc; }
            table { min-width: 100%; table-layout: auto; background-color: #ffffff; border: 1px solid #edf2f7; border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); border-collapse: collapse; margin-top: 24px; }
            thead { background-color: #e2e8f0; }
            th, td { text-align: center; padding: 8px 16px; border-bottom: 1px solid #e2e8f0; }
            th { font-weight: 600; color: #4a5568; }
            .pagination-controls { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; }
            .pagination-controls button { padding: 8px 16px; cursor: pointer; border: 1px solid #cbd5e0; border-radius: 4px; background-color: #edf2f7; }
            .pagination-controls button:disabled { opacity: 0.5; cursor: not-allowed; }
            .badge { display: inline-block; background-color: #f6ad55; color: white; font-weight: 600; padding: 4px 12px; border-radius: 8px; margin-bottom: 16px; }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
            <div style="flex: 1; text-align: center; font-size: 1.5rem; font-weight: 700;">
              ${columnTitle}
            </div>
            <div style="font-size: 1.2rem; color: green;">
              Total Students: ${totalRecords}
            </div>
          </div>
          ${employeeName ? `<div style="text-align: left; font-size: 1.2rem; font-weight: 600; margin-bottom: 16px; color: green;">Team Lead: ${employeeName}</div>` : ""}
          <div class="badge">${getDateLabel()}</div>

          <table>
            <thead>
              <tr>
                ${showEmployeeName ? '<th>Team Lead Name</th>' : ''}
                <th>Enrollment Code</th>
                <th>Student Name</th>
                <th>Mobile Number</th>
                <th>District</th>
                <th>City</th>
                <th>College Name</th>
                <th>Interest Status</th>
                <th>Next Followup Date/Time</th>
                <th>Lead Description</th>
                <th>Active Status</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody id="table-body">
              ${renderTable(currentPage, recordsPerPage)}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="12">
                  <div class="pagination-controls">
                    <span>Total Records: ${totalRecords}</span>
                    <div>
                      <button id="prev-btn" onclick="changePage('prev')" ${currentPage === 1 ? "disabled" : ""}>Previous</button>
                      <span id="page-info">Page ${currentPage} of ${Math.ceil(totalRecords / recordsPerPage)}</span>
                      <button id="next-btn" onclick="changePage('next')" ${currentPage === Math.ceil(totalRecords / recordsPerPage) ? "disabled" : ""}>Next</button>
                    </div>
                    <select id="records-per-page" onchange="changeRecordsPerPage()">
                      <option value="10" ${recordsPerPage === 10 ? "selected" : ""}>show 10</option>
                      <option value="20" ${recordsPerPage === 20 ? "selected" : ""}>show 20</option>
                      <option value="40" ${recordsPerPage === 40 ? "selected" : ""}>show 40</option>
                      <option value="50" ${recordsPerPage === 50 ? "selected" : ""}>show 50</option>
                      <option value="100" ${recordsPerPage === 100 ? "selected" : ""}>show 100</option>
                    </select>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          <script>
            let currentPage = ${currentPage};
            let recordsPerPage = ${recordsPerPage};
            const totalRecords = ${totalRecords};
            const dataArray = ${JSON.stringify(dataArray)};

            const tableBody = document.getElementById('table-body');
            const pageInfo = document.getElementById('page-info');
            const prevBtn = document.getElementById('prev-btn');
            const nextBtn = document.getElementById('next-btn');

            const formatDateTime = (dateTimeString) => {
              if (!dateTimeString) return 'N/A';
              const date = new Date(dateTimeString);
              const options = {
                year: 'numeric',
                month: 'long', // Full month name (e.g., January)
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true, // Use 12-hour format with AM/PM
              };
              return date.toLocaleDateString('en-US', options);
            };

            function renderTable(page, recordsPerPage) {
              const startIndex = (page - 1) * recordsPerPage;
              const endIndex = Math.min(page * recordsPerPage, totalRecords);
              const currentRecords = dataArray.slice(startIndex, endIndex);

              return currentRecords.map(student => \`
                <tr style="border-bottom: 1px solid #e2e8f0; text-align: center;">
                  ${showEmployeeName ? '<td style="padding: 8px 16px;">${student.full_name || \'N/A\'}</td>' : ''}
                  <td style="padding: 8px 16px;">\${student.enrollment_code || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.student_name || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.mobile_number1 || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.district || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.city || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.clg_name || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.is_interested || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${formatDateTime(student.next_followup_date_time)}</td>
                  <td style="padding: 8px 16px;">\${student.lead_description || 'N/A'}</td>
                  <td style="padding: 8px 16px;">\${student.is_active === '1' ? 'Yes' : 'No'}</td>
                  <td style="padding: 8px 16px;">\${formatDateTime(student.created_date)}</td>
                </tr>
              \`).join('');
            }

            function updateTable() {
              tableBody.innerHTML = renderTable(currentPage, recordsPerPage);
              pageInfo.textContent = 'Page ' + currentPage + ' of ' + Math.ceil(totalRecords / recordsPerPage);
              prevBtn.disabled = currentPage === 1;
              nextBtn.disabled = currentPage === Math.ceil(totalRecords / recordsPerPage);
            }

            function changePage(direction) {
              if (direction === 'next' && currentPage < Math.ceil(totalRecords / recordsPerPage)) {
                currentPage++;
              } else if (direction === 'prev' && currentPage > 1) {
                currentPage--;
              }
              updateTable();
            }

            function changeRecordsPerPage() {
              const recordsPerPageSelect = document.getElementById('records-per-page');
              recordsPerPage = parseInt(recordsPerPageSelect.value);
              currentPage = 1;
              updateTable();
            }

            updateTable();
          </script>
        </body>
      </html>
    `;

    newTab.document.write(tableHTML);
    newTab.document.close();
  };


  const finalFilteredAndAggregatedData = getAggregatedData(getFilteredData(reportData));


  return (
    <div className="p-6 bg-white max-w-full">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-2xl font-bold text-gray-900">Team Lead Task Report</h2>

        <div className="flex items-center space-x-2">
          <label htmlFor="filter" className="text-gray-700 text-sm font-medium">
            Select Filter:
          </label>
          <select
            id="filter"
            value={filterRange}
            onChange={(e) => setFilterRange(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            {/* <option value="customRange">Custom Range</option> */}
          </select>

          <button
            onClick={() => setFilterRange("all")}
            className="bg-red-600 text-white rounded px-4 py-2 text-sm hover:bg-red-700 transition"
          >
            Clear Filters
          </button>

          <button
            onClick={fetchTeamLeadReport}
            title="Reload"
            className="bg-blue-600 p-2 rounded hover:bg-blue-700 transition text-white"
          >
            🔄
          </button>

          <button
            onClick={() => alert("Download to Excel clicked")} // implement actual download logic here
            className="bg-green-600 text-white rounded px-4 py-2 text-sm hover:bg-green-700 transition"
          >
            Download to Excel
          </button>
        </div>
      </div>

      {/* Date Badge */}
      <div className="inline-block bg-orange-600 text-white font-semibold px-4 py-2 rounded mb-4">
        {getDateLabel()}
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : finalFilteredAndAggregatedData.length > 0 ? (
        <div className="overflow-x-auto border rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-6 py-3 text-left font-semibold">SNO</th>
                <th className="px-6 py-3 text-left font-semibold">Team Lead Name</th>
                <th className="px-6 py-3 text-left font-semibold">Mobile Number</th>
                <th className="px-6 py-3 text-left font-semibold">Total Follow Up's</th>
                <th className="px-6 py-3 text-left font-semibold">Interested Count</th>
                <th className="px-6 py-3 text-left font-semibold">Most Interested</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-800">
              {finalFilteredAndAggregatedData.map((item, index) => (
                <tr key={item.employee_id}>
                  <td className="px-6 py-4 whitespace-nowrap">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{item.full_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{item.mobile_number}</td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-blue-600 cursor-pointer hover:underline"
                    onClick={() =>
                      openInNewTab(
                        getStudentsForCategory(item.employee_id, "total"),
                        "Total Follow Up's",
                        item.full_name,
                        false
                      )
                    }
                  >
                    {item.total_count}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-green-600 cursor-pointer hover:underline"
                    onClick={() =>
                      openInNewTab(
                        getStudentsForCategory(item.employee_id, "interested"),
                        "Interested Students",
                        item.full_name,
                        false
                      )
                    }
                  >
                    {item.interested_count}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-purple-600 cursor-pointer hover:underline"
                    onClick={() =>
                      openInNewTab(
                        getStudentsForCategory(item.employee_id, "most_interested"),
                        "Most Interested Students",
                        item.full_name,
                        false
                      )
                    }
                  >
                    {item.most_interested_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500">No data available for the selected range.</p>
      )}
    </div>
  );
};

export default TeamLeadTaskReport;